import { AdminAnalyticsActivityType } from '~/api/graphql';
import { pluralize } from '~/lib/pluralize';

export const ACTIVITY_LABELS: Record<AdminAnalyticsActivityType, string> = {
  [AdminAnalyticsActivityType.Call]: 'Calls',
  [AdminAnalyticsActivityType.Workspace]: 'Workspaces',
  [AdminAnalyticsActivityType.Email]: 'Emails',
  [AdminAnalyticsActivityType.KnowledgeBaseUpload]: 'Knowledge base documents'
};

/** Default hourly rate for time-savings dollar conversion ($/hr). */
export const DEFAULT_ANALYTICS_HOURLY_RATE = 60;

/** Default average account premium for incremental revenue estimates ($). */
export const DEFAULT_ANALYTICS_AVG_ACCOUNT_PREMIUM = 10000;

/** How many of the latest periods the analytics charts show. The API's analyticsVisiblePeriods must match. */
export const VISIBLE_PERIODS = 26;

const numberFormatter = new Intl.NumberFormat('en-US');
export const dateFormatter = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
/** For dates the API counts in UTC days, so a UTC-midnight date isn't shown a day early. */
export const utcDateFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC'
});
const utcDateWithYearFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC'
});
export const longDateFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric'
});

/** A moment in the browser's time zone, as "Oct 2, 2:15 PM". */
export const dateTimeFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit'
});

export function formatNumber(value: number) {
  return numberFormatter.format(value);
}

/** The count and its noun, e.g. "1 workspace" or "1,204 workspaces". */
export function countLabel(count: number, noun: string) {
  return `${formatNumber(count)} ${pluralize(count, noun)}`;
}

const utcDay = (date: Date) => date.toISOString().slice(0, 10);

/**
 * Names the UTC dates from after up to, not including, beforeExclusive: "Aug 7 – Sep 5", one
 * date when both fall on the same day, and the years when they differ.
 */
export function formatDayRange(after: Date, beforeExclusive: Date) {
  const lastMoment = new Date(beforeExclusive.getTime() - 1);
  if (utcDay(after) === utcDay(lastMoment)) {
    return utcDateFormatter.format(after);
  }
  const isSameYear = after.getUTCFullYear() === lastMoment.getUTCFullYear();
  const formatter = isSameYear ? utcDateFormatter : utcDateWithYearFormatter;
  return `${formatter.format(after)} – ${formatter.format(lastMoment)}`;
}
